import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AccountTree as WorkflowIcon,
  ChatBubbleOutline as ChatIcon,
  CompareArrows as CompareArrowsIcon,
  ExpandMore as ExpandMoreIcon,
  Functions as FunctionsIcon,
  LibraryBooks as LibraryIcon,
  Memory as MemoryIcon,
  PlayArrow as PlayIcon,
  Psychology as PsychologyIcon,
  PushPin as PinIcon,
  PushPinOutlined as PinOutlinedIcon,
  Schedule as ScheduleIcon,
  Sensors as SensorsIcon,
  Settings as SettingsIcon,
  SmartToy as SmartToyIcon,
  Webhook as WebhookIcon,
} from '@mui/icons-material';
import { Node as ReactFlowNode } from 'reactflow';
import { nodeImportService } from '../../services/nodeImport.service';
import type { ImportedNodeType } from '../../types/nodeImport.types';

const PINNED_KEY = 'wg_pinned_nodes';
const INSTALLED_TYPES_KEY = 'wg_installed_node_types';

function getPinnedTypes(): string[] {
  try {
    return JSON.parse(localStorage.getItem(PINNED_KEY) || '[]');
  } catch {
    return [];
  }
}

function getInstalledNodeTypes(): string[] {
  try {
    return JSON.parse(localStorage.getItem(INSTALLED_TYPES_KEY) || '[]');
  } catch {
    return [];
  }
}

function savePinnedTypes(types: string[]): void {
  localStorage.setItem(PINNED_KEY, JSON.stringify(types));
}

function buildDefaultsFromProperties(
  properties: any[],
  nodeDefaults: Record<string, any> = {}
): Record<string, any> {
  const result: Record<string, any> = { ...nodeDefaults };
  if (!Array.isArray(properties)) return result;
  for (const prop of properties) {
    if (!prop || typeof prop !== 'object' || !prop.name) continue;
    if (prop.type === 'notice' || prop.type === 'hidden') continue;
    if (prop.name in result) continue;
    if ('default' in prop && prop.default !== undefined && prop.default !== null) {
      result[prop.name] = prop.default;
    } else {
      switch (String(prop.type || 'string')) {
        case 'boolean':        result[prop.name] = false; break;
        case 'number':         result[prop.name] = 0; break;
        case 'options':        result[prop.name] = prop.options?.[0]?.value ?? ''; break;
        case 'multiOptions':   result[prop.name] = []; break;
        case 'collection':
        case 'fixedCollection':
        case 'json':
        case 'object':         result[prop.name] = {}; break;
        case 'array':          result[prop.name] = []; break;
        default:               result[prop.name] = '';
      }
    }
  }
  return result;
}

export interface NodeType {
  type: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  category: string;
  nodeType: 'trigger' | 'action' | 'core' | 'imported';
  parameters?: any;
  parametersSchema?: any;
  uiSchema?: any;
  importedProperties?: any[];
  isImported?: boolean;
  validationErrors?: any[];
}

/** Core nodes — always visible in the palette. */
export const CORE_NODES: NodeType[] = [
  {
    type: 'core.set',
    name: 'Set Variable',
    description: 'Set or modify data',
    icon: <FunctionsIcon />,
    category: 'Core',
    nodeType: 'core',
    parameters: { values: {} },
  },
  {
    type: 'core.if',
    name: 'If Condition',
    description: 'Conditional branching',
    icon: <CompareArrowsIcon />,
    category: 'Core',
    nodeType: 'core',
    parameters: { condition: { value1: '', operator: 'equals', value2: '' } },
  },
  {
    type: 'core.switch',
    name: 'Switch',
    description: 'Switch/case matching',
    icon: <CompareArrowsIcon />,
    category: 'Core',
    nodeType: 'core',
    parameters: { expression: '', cases: [{ value: 'A' }, { value: 'B' }], default: 'A' },
  },
  {
    type: 'core.code',
    name: 'Code',
    description: 'Run Python or JavaScript code',
    icon: <FunctionsIcon />,
    category: 'Core',
    nodeType: 'core',
    parameters: { language: 'python', code: 'output = {"result": input.get("value")}' },
  },
  {
    type: 'tool.parse_email',
    name: 'Parse Email',
    description: 'Decode a Gmail / RFC-822 email into headers, text, html, attachments',
    icon: <FunctionsIcon />,
    category: 'Core',
    nodeType: 'core',
    parameters: {
      source: 'auto',
      source_path: '',
      include_html: true,
      include_text: true,
      decode_attachments: false,
      max_text_chars: 0,
    },
  },
  {
    type: 'core.split',
    name: 'Split',
    description: 'Split lists or strings',
    icon: <CompareArrowsIcon />,
    category: 'Core',
    nodeType: 'core',
    parameters: { source_path: '', mode: 'by_items', delimiter: ',', chunk_size: 0 },
  },
  {
    type: 'core.aggregate',
    name: 'Aggregate',
    description: 'Aggregate lists (sum/avg/count)',
    icon: <CompareArrowsIcon />,
    category: 'Core',
    nodeType: 'core',
    parameters: { source_path: '', operation: 'count', field: '', group_by: '' },
  },
  {
    type: 'core.constants',
    name: 'Global Constants',
    description: 'Inject constant values',
    icon: <SettingsIcon />,
    category: 'Core',
    nodeType: 'core',
    parameters: { values: {} },
  },
  {
    type: 'core.delay',
    name: 'Delay',
    description: 'Pause workflow execution',
    icon: <ScheduleIcon />,
    category: 'Core',
    nodeType: 'core',
    parameters: { seconds: 5 },
  },
  {
    type: 'action.webhook.respond',
    name: 'Respond to Webhook',
    description: 'Send a custom response back to the webhook caller',
    icon: <WebhookIcon />,
    category: 'Core',
    nodeType: 'action',
    parameters: {
      respond_with: 'last_node',
      status_code: 200,
      body: '{}',
      headers: {},
    },
  },
];

/** Trigger nodes — always visible in the palette. */
export const TRIGGER_NODES: NodeType[] = [
  {
    type: 'trigger.webhook',
    name: 'Webhook',
    description: 'Triggers workflow on HTTP request',
    icon: <WebhookIcon />,
    category: 'Triggers',
    nodeType: 'trigger',
    parameters: { path: '/webhook', method: 'POST', auth_required: false, auth_token: '' },
  },
  {
    type: 'trigger.schedule',
    name: 'Schedule',
    description: 'Triggers workflow on schedule',
    icon: <ScheduleIcon />,
    category: 'Triggers',
    nodeType: 'trigger',
    parameters: { cron: '0 9 * * *' },
  },
  {
    type: 'trigger.manual',
    name: 'Manual',
    description: 'Manually trigger workflow',
    icon: <PlayIcon />,
    category: 'Triggers',
    nodeType: 'trigger',
    parameters: {},
  },
  {
    type: 'trigger.mqtt_subscribe',
    name: 'MQTT Subscribe',
    description: 'Triggers workflow when a message arrives on an MQTT topic',
    icon: <SensorsIcon />,
    category: 'Triggers',
    nodeType: 'trigger',
    parameters: {
      topic: 'devices/+/events',
      qos: 0,
      payload_type: 'auto',
      credential_id: '',
    },
  },
  {
    type: 'n8n-nodes-base.chatTrigger',
    name: 'Chat Trigger',
    description: 'Start workflow from chat input',
    icon: <ChatIcon />,
    category: 'Triggers',
    nodeType: 'trigger',
    parameters: { sessionIdType: 'customKey', sessionKey: '{{sessionId}}' },
  },
];

/** All integration / action / AI nodes (go into the Library, not the default palette). */
export const ALL_LIBRARY_NODES: NodeType[] = [
  // Actions
  { type: 'action.http', name: 'HTTP Request', description: 'Make HTTP requests to APIs', icon: <WorkflowIcon />, category: 'Actions', nodeType: 'action', parameters: { url: 'https://api.example.com', method: 'GET', headers: {}, body: {} } },
  { type: 'action.database', name: 'Database Query', description: 'Execute SQL queries', icon: <WorkflowIcon />, category: 'Actions', nodeType: 'action', parameters: { query: 'SELECT * FROM users', query_type: 'select' } },
  { type: 'action.postgresql', name: 'PostgreSQL', description: 'Query an external PostgreSQL database', icon: <WorkflowIcon />, category: 'Actions', nodeType: 'action', parameters: { credential_id: '', host: '', port: 5432, database: '', user: '', password: '', ssl_mode: 'prefer', query: 'SELECT 1', query_type: 'select' } },
  { type: 'action.supabase', name: 'Supabase', description: 'Supabase REST operations', icon: <WorkflowIcon />, category: 'Actions', nodeType: 'action', parameters: { credential_id: '', base_url: '', api_key: '', table: '', operation: 'select', select: '*', filters: {}, values: {}, limit: 10 } },
  { type: 'action.qdrant', name: 'Qdrant', description: 'Vector search / upsert', icon: <WorkflowIcon />, category: 'Actions', nodeType: 'action', parameters: { base_url: '', api_key: '', operation: 'search', collection: '', vector: [], top_k: 5 } },
  // Communication
  { type: 'action.email', name: 'Send Email', description: 'Send email via SMTP', icon: <WorkflowIcon />, category: 'Communication', nodeType: 'action', parameters: { credential_id: '', from_email: '', to_email: '', subject: '', body: '' } },
  { type: 'action.smtp', name: 'SMTP', description: 'Send email via SMTP', icon: <WorkflowIcon />, category: 'Communication', nodeType: 'action', parameters: { credential_id: '', host: '', port: 587, username: '', password: '', use_tls: true, from_email: '', to_email: '', subject: '', body: '' } },
  { type: 'action.imap', name: 'IMAP', description: 'Fetch email via IMAP', icon: <WorkflowIcon />, category: 'Communication', nodeType: 'action', parameters: { credential_id: '', host: '', port: 993, username: '', password: '', mailbox: 'INBOX', search: 'ALL', limit: 10, use_ssl: true } },
  { type: 'action.mqtt_publish', name: 'MQTT Publish', description: 'Publish a message to an MQTT broker (Node-RED `mqtt out` equivalent)', icon: <WorkflowIcon />, category: 'Communication', nodeType: 'action', parameters: { credential_id: '', broker_host: '', broker_port: 1883, username: '', password: '', client_id: '', use_tls: false, topic: '', payload: '', qos: 0, retain: false } },
  { type: 'action.gmail', name: 'Gmail', description: 'Send or list Gmail messages', icon: <WorkflowIcon />, category: 'Communication', nodeType: 'action', parameters: { credential_id: '', access_token: '', operation: 'list', query: '', to_email: '', subject: '', body: '' } },
  { type: 'action.slack', name: 'Slack', description: 'Send messages, manage channels & users via Slack API', icon: <WorkflowIcon />, category: 'Communication', nodeType: 'action', parameters: { credential_id: '', operation: 'send_message', channel: '', text: '', thread_ts: '' } },
  { type: 'action.discord', name: 'Discord', description: 'Send messages via Discord webhook or bot', icon: <WorkflowIcon />, category: 'Communication', nodeType: 'action', parameters: { credential_id: '', operation: 'send_message', content: '', username: '', channel_id: '' } },
  { type: 'action.microsoft_teams', name: 'Microsoft Teams', description: 'Send messages via Teams Incoming Webhook', icon: <WorkflowIcon />, category: 'Communication', nodeType: 'action', parameters: { credential_id: '', operation: 'send_message', text: '', title: '' } },
  { type: 'action.zoom', name: 'Zoom', description: 'Create and manage Zoom meetings', icon: <WorkflowIcon />, category: 'Communication', nodeType: 'action', parameters: { credential_id: '', operation: 'create_meeting', topic: '', start_time: '', duration: 60 } },
  // Productivity
  { type: 'action.google_sheet', name: 'Google Sheets', description: 'Read or write Google Sheets', icon: <WorkflowIcon />, category: 'Productivity', nodeType: 'action', parameters: { access_token: '', spreadsheet_id: '', range: 'Sheet1!A1:C10', operation: 'get' } },
  { type: 'action.google_drive', name: 'Google Drive', description: 'List, upload, or download files', icon: <WorkflowIcon />, category: 'Productivity', nodeType: 'action', parameters: { access_token: '', operation: 'list', query: '' } },
  { type: 'action.google_calendar', name: 'Google Calendar', description: 'Create, list and manage Google Calendar events', icon: <WorkflowIcon />, category: 'Productivity', nodeType: 'action', parameters: { credential_id: '', operation: 'list_events', calendar_id: 'primary' } },
  { type: 'action.notion', name: 'Notion', description: 'Create pages and query Notion databases', icon: <WorkflowIcon />, category: 'Productivity', nodeType: 'action', parameters: { credential_id: '', operation: 'create_page', parent_id: '' } },
  { type: 'action.airtable', name: 'Airtable', description: 'List, create and update Airtable records', icon: <WorkflowIcon />, category: 'Productivity', nodeType: 'action', parameters: { credential_id: '', operation: 'list_records', base_id: '', table: '' } },
  { type: 'action.dropbox', name: 'Dropbox', description: 'Upload, download and manage Dropbox files', icon: <WorkflowIcon />, category: 'Productivity', nodeType: 'action', parameters: { credential_id: '', operation: 'list_folder', path: '' } },
  { type: 'action.cal_com', name: 'Cal.com', description: 'Create and manage Cal.com bookings', icon: <WorkflowIcon />, category: 'Productivity', nodeType: 'action', parameters: { credential_id: '', operation: 'list_bookings' } },
  { type: 'action.microsoft365', name: 'Microsoft 365', description: 'Send email, manage calendar, Teams & OneDrive via Graph API', icon: <WorkflowIcon />, category: 'Productivity', nodeType: 'action', parameters: { credential_id: '', operation: 'send_email', user_id: 'me', to: '', subject: '', body: '' } },
  // Project Management
  { type: 'action.jira', name: 'Jira', description: 'Create, update and search Jira issues', icon: <WorkflowIcon />, category: 'Project Management', nodeType: 'action', parameters: { credential_id: '', operation: 'create_issue', project_key: '', summary: '', issue_type: 'Task' } },
  { type: 'action.confluence', name: 'Confluence', description: 'Create and manage Confluence pages', icon: <WorkflowIcon />, category: 'Project Management', nodeType: 'action', parameters: { credential_id: '', operation: 'create_page', space_key: '', title: '' } },
  { type: 'action.asana', name: 'Asana', description: 'Manage tasks, projects and workspaces', icon: <WorkflowIcon />, category: 'Project Management', nodeType: 'action', parameters: { credential_id: '', operation: 'list_tasks', workspace: '' } },
  { type: 'action.trello', name: 'Trello', description: 'Manage boards, lists, cards & members', icon: <WorkflowIcon />, category: 'Project Management', nodeType: 'action', parameters: { credential_id: '', operation: 'list_boards' } },
  // CRM
  { type: 'action.hubspot', name: 'HubSpot', description: 'Manage HubSpot CRM contacts, deals & companies', icon: <WorkflowIcon />, category: 'CRM', nodeType: 'action', parameters: { credential_id: '', operation: 'list_contacts', object_type: 'contact', limit: 100 } },
  { type: 'action.shopify', name: 'Shopify', description: 'Manage Shopify products, orders & customers', icon: <WorkflowIcon />, category: 'CRM', nodeType: 'action', parameters: { credential_id: '', operation: 'list_products', resource: 'product', api_version: '2024-01', limit: 50 } },
  { type: 'action.zoho_crm', name: 'Zoho CRM', description: 'Create and manage Zoho CRM records', icon: <WorkflowIcon />, category: 'CRM', nodeType: 'action', parameters: { credential_id: '', operation: 'create_record', module: 'Leads', data: {} } },
  { type: 'action.pipedrive', name: 'Pipedrive', description: 'Manage CRM deals, persons & organizations', icon: <WorkflowIcon />, category: 'CRM', nodeType: 'action', parameters: { credential_id: '', operation: 'list_deals', resource: 'deal', limit: 100 } },
  { type: 'action.insightly', name: 'Insightly', description: 'Manage Insightly CRM contacts and leads', icon: <WorkflowIcon />, category: 'CRM', nodeType: 'action', parameters: { credential_id: '', operation: 'create_contact' } },
  // ERP
  { type: 'action.odoo', name: 'Odoo', description: 'Create, update, or query Odoo via JSON-RPC', icon: <SettingsIcon />, category: 'ERP', nodeType: 'action', parameters: { base_url: 'https://your-odoo.com', db: '', username: '', api_key: '', model: 'res.partner', operation: 'search_read' } },
  { type: 'action.erpnext', name: 'ERPNext', description: 'Create and manage ERPNext documents', icon: <WorkflowIcon />, category: 'ERP', nodeType: 'action', parameters: { credential_id: '', operation: 'create_document', doctype: '' } },
  { type: 'action.netsuite', name: 'NetSuite', description: 'Query & manage NetSuite records via REST API', icon: <WorkflowIcon />, category: 'ERP', nodeType: 'action', parameters: { credential_id: '', operation: 'run_suiteql', suiteql: 'SELECT id, companyName FROM customer' } },
  { type: 'action.quickbooks', name: 'QuickBooks', description: 'Query & manage QuickBooks customers, invoices & payments', icon: <WorkflowIcon />, category: 'ERP', nodeType: 'action', parameters: { credential_id: '', operation: 'query', entity: 'Customer' } },
  { type: 'action.sap', name: 'SAP', description: 'Query & manage SAP OData entities (S/4HANA, B1, ECC)', icon: <WorkflowIcon />, category: 'ERP', nodeType: 'action', parameters: { credential_id: '', operation: 'query', service_path: '', entity_set: '' } },
  { type: 'action.xero', name: 'Xero', description: 'Manage Xero invoices, contacts, items & bank transactions', icon: <WorkflowIcon />, category: 'ERP', nodeType: 'action', parameters: { credential_id: '', operation: 'list_invoices', resource: 'invoice' } },
  { type: 'action.myob', name: 'MYOB', description: 'Manage MYOB AccountRight invoices, customers, suppliers & items', icon: <WorkflowIcon />, category: 'ERP', nodeType: 'action', parameters: { credential_id: '', operation: 'list_company_files', resource: 'customer', company_file_id: '' } },
  // Marketing
  { type: 'action.mailchimp', name: 'Mailchimp', description: 'Manage Mailchimp subscribers and campaigns', icon: <WorkflowIcon />, category: 'Marketing', nodeType: 'action', parameters: { credential_id: '', operation: 'add_subscriber', list_id: '', email: '', status: 'subscribed' } },
  // AI
  { type: 'action.llm.chat', name: 'LLM Chat', description: 'Call your configured LLM (set API key in Settings)', icon: <SmartToyIcon />, category: 'AI', nodeType: 'action', parameters: { prompt: 'Write a short response to: {{body.question}}', system_prompt: '', model: '' } },
  { type: '@n8n/n8n-nodes-langchain.agent', name: 'AI Agent', description: 'Agent with tools + memory (n8n compatible)', icon: <PsychologyIcon />, category: 'AI', nodeType: 'action', parameters: { text: '{{chatInput}}', systemMessage: '', maxIterations: 3 } },
  { type: '@n8n/n8n-nodes-langchain.lmChatOpenAi', name: 'OpenAI Chat Model', description: 'Model config for AI Agent', icon: <SmartToyIcon />, category: 'AI', nodeType: 'action', parameters: { model: 'gpt-4o-mini' } },
  { type: '@n8n/n8n-nodes-langchain.embeddingsOpenAi', name: 'OpenAI Embeddings', description: 'Embeddings provider for vector tools', icon: <SmartToyIcon />, category: 'AI', nodeType: 'action', parameters: { model: 'text-embedding-3-small' } },
  // AI Tools
  { type: '@n8n/n8n-nodes-langchain.memoryBufferWindow', name: 'Memory Buffer', description: 'Chat memory by session id', icon: <MemoryIcon />, category: 'AI Tools', nodeType: 'action', parameters: { sessionKey: '{{sessionId}}', sessionIdType: 'customKey', windowSize: 10 } },
  { type: 'n8n-nodes-base.postgresTool', name: 'Postgres Tool', description: 'Read-only SQL tool for agent', icon: <WorkflowIcon />, category: 'AI Tools', nodeType: 'action', parameters: { toolDescription: 'Query the Postgres database' } },
  { type: '@n8n/n8n-nodes-langchain.vectorStorePGVector', name: 'PGVector Store', description: 'Vector store retriever tool', icon: <WorkflowIcon />, category: 'AI Tools', nodeType: 'action', parameters: { tableName: 'n8n_knowledges', topK: 10, toolDescription: 'Retrieve knowledge base matches' } },
  { type: '@n8n/n8n-nodes-langchain.toolThink', name: 'Think Tool', description: 'Lightweight reasoning tool', icon: <PsychologyIcon />, category: 'AI Tools', nodeType: 'action', parameters: {} },
];

/** All built-in nodes (used for full-text search within palette). */
export const ALL_BUILTIN_NODES: NodeType[] = [...TRIGGER_NODES, ...CORE_NODES, ...ALL_LIBRARY_NODES];

interface NodePaletteProps {
  onNodeAdd: (node: ReactFlowNode) => void;
  onOpenLibrary?: () => void;
  disabled?: boolean;
}

/** Convert a NodeType definition into a ReactFlow node. */
export function nodeTypeToReactFlowNode(nodeType: NodeType): ReactFlowNode {
  return {
    id: `${nodeType.type}_${Date.now()}`,
    type: nodeType.nodeType,
    position: { x: Math.random() * 400, y: Math.random() * 400 },
    data: {
      label: nodeType.name,
      nodeType: nodeType.type,
      parameters: nodeType.parameters || {},
      description: nodeType.description,
      parametersSchema: nodeType.parametersSchema,
      uiSchema: nodeType.uiSchema,
      ...(nodeType.isImported
        ? {
            isImported: true,
            validationErrors: nodeType.validationErrors || [],
            importedProperties: nodeType.importedProperties || [],
          }
        : {}),
    },
  };
}

export const NodePalette: React.FC<NodePaletteProps> = ({ onNodeAdd, onOpenLibrary, disabled }) => {
  const [importedNodes, setImportedNodes] = useState<ImportedNodeType[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [pinnedTypes, setPinnedTypes] = useState<string[]>(getPinnedTypes);
  const [installedNodeTypes, setInstalledNodeTypes] = useState<string[]>(getInstalledNodeTypes);

  const loadImportedNodes = useCallback(async () => {
    try {
      const resp = await nodeImportService.getActiveNodes();
      setImportedNodes(resp.nodes || []);
    } catch {
      // palette still works without imported nodes
    }
  }, []);

  useEffect(() => {
    loadImportedNodes();
  }, [loadImportedNodes]);

  useEffect(() => {
    // Reload imported nodes when the window regains focus (e.g. user installed from Registry page)
    const onFocus = () => loadImportedNodes();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [loadImportedNodes]);

  useEffect(() => {
    // Reload when localStorage changes (from Library Drawer or Registry page)
    const onStorage = (e: StorageEvent) => {
      if (e.key === PINNED_KEY) {
        setPinnedTypes(getPinnedTypes());
      }
      if (e.key === INSTALLED_TYPES_KEY) {
        setInstalledNodeTypes(getInstalledNodeTypes());
      }
      if (e.key === 'wg_registry_installed_nodes') {
        loadImportedNodes();
        setInstalledNodeTypes(getInstalledNodeTypes());
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [loadImportedNodes]);

  // Registry-installed built-in nodes shown grouped by category
  const installedLibraryNodes: NodeType[] = useMemo(() => {
    return installedNodeTypes
      .map((t) => ALL_LIBRARY_NODES.find((n) => n.type === t))
      .filter(Boolean) as NodeType[];
  }, [installedNodeTypes]);

  const installedByCategory: Record<string, NodeType[]> = useMemo(() => {
    return installedLibraryNodes.reduce((acc, node) => {
      const cat = node.category || 'Actions';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(node);
      return acc;
    }, {} as Record<string, NodeType[]>);
  }, [installedLibraryNodes]);

  // npm-imported nodes (exclude ones already shown as installed built-ins to avoid duplicates)
  const importedNodeTypes: NodeType[] = useMemo(() => {
    return importedNodes
      .filter((n) => !installedNodeTypes.includes(n.name))
      .map((n) => {
        const props: any[] = n.properties || n.original_n8n_data?.properties || [];
        return {
          type: n.name,
          name: n.display_name,
          description: n.description || 'Imported node',
          icon: <WorkflowIcon />,
          category: 'Imported Nodes',
          nodeType: 'imported' as const,
          parameters: buildDefaultsFromProperties(props, n.defaults || {}),
          parametersSchema: n.parameters_schema || {},
          uiSchema: n.ui_schema || {},
          importedProperties: props,
          isImported: true,
          validationErrors: n.validation_errors || [],
        };
      });
  }, [importedNodes, installedNodeTypes]);

  const pinnedNodes: NodeType[] = useMemo(() => {
    const allBuiltin = ALL_BUILTIN_NODES;
    return pinnedTypes
      .map((t) => allBuiltin.find((n) => n.type === t))
      .filter(Boolean) as NodeType[];
  }, [pinnedTypes]);

  const togglePin = useCallback((nodeType: string) => {
    setPinnedTypes((prev) => {
      const next = prev.includes(nodeType)
        ? prev.filter((t) => t !== nodeType)
        : [...prev, nodeType];
      savePinnedTypes(next);
      return next;
    });
  }, []);

  const isPinned = useCallback(
    (nodeType: string) => pinnedTypes.includes(nodeType),
    [pinnedTypes]
  );

  const handleNodeClick = useCallback(
    (nodeType: NodeType) => {
      if (disabled) return;
      onNodeAdd(nodeTypeToReactFlowNode(nodeType));
    },
    [disabled, onNodeAdd]
  );

  // When searching, show all node types (including library nodes)
  const isSearching = searchTerm.trim().length > 0;
  const normalized = searchTerm.trim().toLowerCase();

  const searchResults: NodeType[] = useMemo(() => {
    if (!isSearching) return [];
    const pool = [...ALL_BUILTIN_NODES, ...importedNodeTypes];
    return pool.filter((n) =>
      `${n.name} ${n.type} ${n.description}`.toLowerCase().includes(normalized)
    );
  }, [isSearching, normalized, importedNodeTypes]);

  const groupedSearchResults = useMemo(() => {
    return searchResults.reduce((acc, node) => {
      if (!acc[node.category]) acc[node.category] = [];
      acc[node.category].push(node);
      return acc;
    }, {} as Record<string, NodeType[]>);
  }, [searchResults]);

  const renderNodeItem = (node: NodeType, showPin = false) => (
    <ListItem key={node.type} disablePadding secondaryAction={
      showPin ? (
        <Tooltip title={isPinned(node.type) ? 'Unpin from palette' : 'Pin to palette'}>
          <IconButton
            size="small"
            edge="end"
            onClick={(e) => { e.stopPropagation(); togglePin(node.type); }}
          >
            {isPinned(node.type)
              ? <PinIcon fontSize="small" color="primary" />
              : <PinOutlinedIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      ) : undefined
    }>
      <ListItemButton
        onClick={() => handleNodeClick(node)}
        sx={{ py: 0.75, pr: showPin ? 5 : undefined }}
        disabled={disabled}
      >
        <ListItemIcon sx={{ minWidth: 36, color: 'primary.main' }}>
          {node.icon}
        </ListItemIcon>
        <ListItemText
          primary={<Typography variant="body2" sx={{ fontWeight: 'medium' }}>{node.name}</Typography>}
          secondary={<Typography variant="caption" color="text.secondary">{node.description}</Typography>}
        />
      </ListItemButton>
    </ListItem>
  );

  return (
    <Paper sx={{ height: '100%', flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', borderRadius: 0 }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" gutterBottom>Node Palette</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Click to add nodes to your workflow
        </Typography>
        <TextField
          size="small"
          fullWidth
          placeholder="Search all nodes..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 1 }}
        />
        <Button
          size="small"
          fullWidth
          variant="outlined"
          startIcon={<LibraryIcon />}
          onClick={onOpenLibrary}
          disabled={disabled}
          sx={{ fontWeight: 'medium' }}
        >
          Browse Node Library
        </Button>
        {importedNodes.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Chip size="small" label={`${importedNodes.length} npm nodes installed`} />
          </Box>
        )}
      </Box>

      <Box>
        {/* Search results (shows all nodes including library) */}
        {isSearching && (
          <>
            {searchResults.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No nodes found. Try browsing the library.
                </Typography>
                <Button size="small" startIcon={<LibraryIcon />} onClick={onOpenLibrary} sx={{ mt: 1 }}>
                  Browse Library
                </Button>
              </Box>
            ) : (
              Object.entries(groupedSearchResults).map(([category, nodes]) => (
                <Accordion key={category} defaultExpanded disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{category}</Typography>
                    <Chip size="small" label={nodes.length} sx={{ ml: 1 }} />
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    <List dense>
                      {nodes.map((n) => renderNodeItem(n, !CORE_NODES.find(c => c.type === n.type) && !TRIGGER_NODES.find(t => t.type === n.type)))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              ))
            )}
          </>
        )}

        {/* Default view (not searching) */}
        {!isSearching && (
          <>
            {/* Pinned section */}
            {pinnedNodes.length > 0 && (
              <Accordion defaultExpanded disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    Pinned
                  </Typography>
                  <Chip size="small" label={pinnedNodes.length} sx={{ ml: 1 }} />
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <List dense>
                    {pinnedNodes.map((n) => renderNodeItem(n, true))}
                  </List>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Triggers */}
            <Accordion defaultExpanded disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Triggers</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <List dense>
                  {TRIGGER_NODES.map((n) => renderNodeItem(n))}
                </List>
              </AccordionDetails>
            </Accordion>

            {/* Core */}
            <Accordion defaultExpanded disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Core</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <List dense>
                  {CORE_NODES.map((n) => renderNodeItem(n))}
                </List>
              </AccordionDetails>
            </Accordion>

            {/* Installed from Node Library (grouped by category) */}
            {Object.entries(installedByCategory).map(([category, nodes]) => (
              <Accordion key={`installed-${category}`} defaultExpanded disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{category}</Typography>
                  <Chip size="small" label={nodes.length} sx={{ ml: 1 }} />
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <List dense>
                    {nodes.map((n) => renderNodeItem(n, true))}
                  </List>
                </AccordionDetails>
              </Accordion>
            ))}

            {/* Imported npm nodes (non-built-in) */}
            {importedNodeTypes.length > 0 && (
              <Accordion defaultExpanded disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Imported Nodes</Typography>
                  <Chip size="small" label={importedNodeTypes.length} sx={{ ml: 1 }} />
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <List dense>
                    {importedNodeTypes.map((n) => renderNodeItem(n))}
                  </List>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Hint when palette is clean */}
            {pinnedNodes.length === 0 && installedLibraryNodes.length === 0 && importedNodeTypes.length === 0 && (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Browse the library to find nodes (Slack, HTTP Request, etc.) and install them here.
                </Typography>
                <Button size="small" variant="outlined" startIcon={<LibraryIcon />} onClick={onOpenLibrary} disabled={disabled}>
                  Browse Library
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>
    </Paper>
  );
};
